import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColDef, SelectionChangedEvent, IRowNode, ValueFormatterParams } from 'ag-grid-community';
import {
  ArrowLeft,
  Users,
  Shield,
  Package,
  Plus,
  Trash2,
  X,
  Search,
  Save,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessControl } from '@/contexts/AccessControlContext';
import {
  groupService,
  groupMemberService,
  groupPermissionService,
  groupModuleService,
  userAdminService,
  permissionAdminService,
  profileService,
  type GroupRow,
  type UserProfileRow,
  type GroupMemberRow,
  type GroupPermissionRow,
  type PermissionRow,
  type GroupModuleRow,
  type ServiceContext,
} from '@/services';
import { usePagination } from '@/hooks/usePagination';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import RecordMetadata from '@/components/RecordMetadata';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return '\u2014';
  }
}

/* ------------------------------------------------------------------ */
/*  Merged member type (profile + assignment date)                     */
/* ------------------------------------------------------------------ */

interface MergedMember {
  profile_id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  is_active: boolean;
  assigned_at: string;
}

/* ------------------------------------------------------------------ */
/*  Add User Drawer                                                    */
/* ------------------------------------------------------------------ */

interface AddUserDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableUsers: UserProfileRow[];
  isAdding: string | null;
  onAdd: (user: UserProfileRow) => void;
}

function AddUserDrawer({
  open,
  onOpenChange,
  availableUsers,
  isAdding,
  onAdd,
}: AddUserDrawerProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return availableUsers;
    const q = search.toLowerCase();
    return availableUsers.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [availableUsers, search]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[440px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Add User</SheetTitle>
          <SheetDescription>
            Click a user to add them to this group.
          </SheetDescription>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={search ? 'pl-9 pr-8' : 'pl-9'}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {availableUsers.length === 0
                ? 'All users are already in this group.'
                : 'No matching users found.'}
            </p>
          ) : (
            <div className="space-y-1 pb-4">
              {filtered.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  disabled={isAdding === u.id}
                  onClick={() => onAdd(u)}
                  className="w-full flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors text-left disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <span className="font-medium">{u.full_name || 'Unnamed'}</span>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Plus className="h-4 w-4 shrink-0 ml-2 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/*  Add Permission Drawer                                              */
/* ------------------------------------------------------------------ */

interface AddPermissionDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availablePermissions: PermissionRow[];
  isAdding: string | null;
  onAdd: (permission: PermissionRow) => void;
}

function AddPermissionDrawer({
  open,
  onOpenChange,
  availablePermissions,
  isAdding,
  onAdd,
}: AddPermissionDrawerProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return availablePermissions;
    const q = search.toLowerCase();
    return availablePermissions.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        (p.category?.toLowerCase().includes(q) ?? false)
    );
  }, [availablePermissions, search]);

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, PermissionRow[]>>((acc, p) => {
      const cat = p.category || 'Uncategorized';
      (acc[cat] ??= []).push(p);
      return acc;
    }, {});
  }, [filtered]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[440px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Add Permission</SheetTitle>
          <SheetDescription>
            Click a permission to assign it to this group.
          </SheetDescription>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter permissions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={search ? 'pl-9 pr-8' : 'pl-9'}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {availablePermissions.length === 0
                ? 'All permissions are already assigned.'
                : 'No matching permissions found.'}
            </p>
          ) : (
            <div className="space-y-4 pb-4">
              {Object.entries(grouped)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, perms]) => (
                  <div key={category}>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {category}
                    </h4>
                    <div className="space-y-1">
                      {perms.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          disabled={isAdding === p.id}
                          onClick={() => onAdd(p)}
                          className="w-full flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors text-left disabled:opacity-50"
                        >
                          <div className="min-w-0">
                            <span className="font-medium">{p.name}</span>
                            <span className="ml-2 text-xs text-muted-foreground font-mono">
                              {p.code}
                            </span>
                            {p.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {p.description}
                              </p>
                            )}
                          </div>
                          <Plus className="h-4 w-4 shrink-0 ml-2 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/*  Users Tab                                                          */
/* ------------------------------------------------------------------ */

interface UsersTabProps {
  groupId: string;
  companyId: string;
  context: ServiceContext;
}

function UsersTab({ groupId, companyId, context }: UsersTabProps) {
  const queryClient = useQueryClient();
  const { invalidateCache: invalidateAccessCache } = useAccessControl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<MergedMember[]>([]);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'group-detail-users' });

  // Group member assignments
  const { data: memberRows, isLoading: membersLoading } = useQuery({
    queryKey: ['group-members', groupId, companyId],
    queryFn: async () => {
      const r = await groupMemberService.listMembers(groupId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 30_000,
  });

  // All company users (for merging + drawer)
  const { data: allUsers } = useQuery({
    queryKey: ['company-users', companyId],
    queryFn: async () => {
      const r = await userAdminService.listUsers(companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  // Merge member assignments with profile data
  const members = useMemo<MergedMember[]>(() => {
    if (!memberRows || !allUsers) return [];
    const userMap = new Map(allUsers.map((u) => [u.id, u]));
    return memberRows
      .map((m) => {
        const profile = userMap.get(m.profile_id);
        if (!profile) return null;
        return {
          profile_id: m.profile_id,
          full_name: profile.full_name,
          email: profile.email,
          phone: profile.phone,
          is_active: !profile.deleted_at,
          assigned_at: m.created,
        };
      })
      .filter(Boolean) as MergedMember[];
  }, [memberRows, allUsers]);

  // Client-side search
  const filteredMembers = useMemo(() => {
    if (!debouncedSearch) return members;
    const q = debouncedSearch.toLowerCase();
    return members.filter(
      (m) =>
        m.full_name?.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q)
    );
  }, [members, debouncedSearch]);

  // Client-side pagination
  const paginatedMembers = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredMembers.slice(start, start + pageSize);
  }, [filteredMembers, pageIndex, pageSize]);

  // Available users for drawer (not already in group)
  const availableUsers = useMemo(() => {
    if (!allUsers) return [];
    const memberIds = new Set((memberRows ?? []).map((m) => m.profile_id));
    return allUsers.filter((u) => !u.deleted_at && !memberIds.has(u.id));
  }, [allUsers, memberRows]);

  const columnDefs = useMemo<ColDef<MergedMember>[]>(
    () => [
      { headerName: 'Name', field: 'full_name', flex: 1, minWidth: 180 },
      { headerName: 'Email', field: 'email', flex: 1, minWidth: 200 },
      { headerName: 'Phone', field: 'phone', width: 150 },
      {
        headerName: 'Active',
        field: 'is_active',
        width: 100,
        cellRenderer: (params: { data: MergedMember | undefined }) => {
          if (!params.data) return null;
          return (
            <span className={params.data.is_active ? 'text-green-600' : 'text-muted-foreground'}>
              {params.data.is_active ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        headerName: 'Added',
        field: 'assigned_at',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<MergedMember>) =>
          formatDateTime(params.value),
      },
    ],
    []
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<MergedMember>) => {
      setSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  const handleAddUser = useCallback(
    async (user: UserProfileRow) => {
      setIsAdding(user.id);
      try {
        const result = await groupMemberService.addMember(groupId, user.id, context);
        if (result.error) {
          toast.error(`Failed to add: ${result.error.message}`);
        } else {
          toast.success(`Added "${user.full_name || user.email}"`);
          queryClient.invalidateQueries({ queryKey: ['group-members', groupId, companyId] });
          invalidateAccessCache();
        }
      } finally {
        setIsAdding(null);
      }
    },
    [groupId, companyId, context, queryClient, invalidateAccessCache]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (selectedRows.length === 0) return;
    setIsDeleting(true);
    try {
      const ids = selectedRows.map((r) => r.profile_id);
      const result = await groupMemberService.removeBulkMembers(groupId, ids, companyId);
      if (result.error) {
        toast.error(`Failed to remove: ${result.error.message}`);
      } else {
        toast.success(`Removed ${selectedRows.length} user(s)`);
        setSelectedRows([]);
        queryClient.invalidateQueries({ queryKey: ['group-members', groupId, companyId] });
        invalidateAccessCache();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRows, groupId, companyId, queryClient, invalidateAccessCache]);

  return (
    <>
      <p className="text-sm text-muted-foreground mb-4">Users assigned to this group</p>
      <AGDataGrid<MergedMember>
        rowData={paginatedMembers}
        columnDefs={columnDefs}
        totalCount={filteredMembers.length}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search users..."
        isLoading={membersLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onSelectionChanged={handleSelectionChanged}
        rowSelection={{
          mode: 'multiRow' as const,
          checkboxes: true,
          headerCheckbox: true,
        }}
        height="calc(100vh - 480px)"
        toolbarActions={
          <div className="flex items-center gap-2">
            {selectedRows.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove ({selectedRows.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        }
      />

      <AddUserDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        availableUsers={availableUsers}
        isAdding={isAdding}
        onAdd={handleAddUser}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Permissions Tab                                                    */
/* ------------------------------------------------------------------ */

interface PermissionsTabProps {
  groupId: string;
  companyId: string;
  context: ServiceContext;
}

function PermissionsTab({ groupId, companyId, context }: PermissionsTabProps) {
  const queryClient = useQueryClient();
  const { invalidateCache: invalidateAccessCache } = useAccessControl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GroupPermissionRow[]>([]);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'group-detail-perms' });

  // Group permission assignments
  const { data: groupPermissions, isLoading: permsLoading } = useQuery({
    queryKey: ['group-permissions', groupId, companyId],
    queryFn: async () => {
      const r = await groupPermissionService.listPermissions(groupId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 30_000,
  });

  // All company permissions (for the drawer)
  const { data: allPermissions } = useQuery({
    queryKey: ['company-permissions', companyId],
    queryFn: async () => {
      const r = await permissionAdminService.list(companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const permissions = groupPermissions ?? [];

  // Client-side search
  const filteredPerms = useMemo(() => {
    if (!debouncedSearch) return permissions;
    const q = debouncedSearch.toLowerCase();
    return permissions.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        (p.category?.toLowerCase().includes(q) ?? false)
    );
  }, [permissions, debouncedSearch]);

  // Client-side pagination
  const paginatedPerms = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredPerms.slice(start, start + pageSize);
  }, [filteredPerms, pageIndex, pageSize]);

  // Available permissions for drawer
  const availablePermissions = useMemo(() => {
    if (!allPermissions) return [];
    const assignedIds = new Set(permissions.map((p) => p.permission_id));
    return allPermissions.filter((p) => p.is_active && !assignedIds.has(p.id));
  }, [allPermissions, permissions]);

  const columnDefs = useMemo<ColDef<GroupPermissionRow>[]>(
    () => [
      { headerName: 'Name', field: 'name', flex: 1, minWidth: 180 },
      { headerName: 'Code', field: 'code', width: 180, cellClass: 'font-mono text-xs' },
      {
        headerName: 'System',
        field: 'is_system',
        width: 100,
        cellRenderer: (params: { data: GroupPermissionRow | undefined }) => {
          if (!params.data) return null;
          return params.data.is_system ? (
            <Badge variant="secondary" className="gap-1">System</Badge>
          ) : null;
        },
      },
      {
        headerName: 'Active',
        field: 'is_active',
        width: 100,
        cellRenderer: (params: { data: GroupPermissionRow | undefined }) => {
          if (!params.data) return null;
          return (
            <span className={params.data.is_active ? 'text-green-600' : 'text-muted-foreground'}>
              {params.data.is_active ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        headerName: 'Added',
        field: 'assigned_at',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<GroupPermissionRow>) =>
          formatDateTime(params.value),
      },
    ],
    []
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<GroupPermissionRow>) => {
      setSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  const handleAddPermission = useCallback(
    async (permission: PermissionRow) => {
      setIsAdding(permission.id);
      try {
        const result = await groupPermissionService.addPermission(groupId, permission.id, context);
        if (result.error) {
          toast.error(`Failed to add: ${result.error.message}`);
        } else {
          toast.success(`Added "${permission.name}"`);
          queryClient.invalidateQueries({ queryKey: ['group-permissions', groupId, companyId] });
          invalidateAccessCache();
        }
      } finally {
        setIsAdding(null);
      }
    },
    [groupId, companyId, context, queryClient, invalidateAccessCache]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (selectedRows.length === 0) return;
    setIsDeleting(true);
    try {
      const ids = selectedRows.map((r) => r.permission_id);
      const result = await groupPermissionService.removeBulkPermissions(groupId, ids, companyId);
      if (result.error) {
        toast.error(`Failed to remove: ${result.error.message}`);
      } else {
        toast.success(`Removed ${selectedRows.length} permission(s)`);
        setSelectedRows([]);
        queryClient.invalidateQueries({ queryKey: ['group-permissions', groupId, companyId] });
        invalidateAccessCache();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRows, groupId, companyId, queryClient, invalidateAccessCache]);

  return (
    <>
      <p className="text-sm text-muted-foreground mb-4">Permissions granted to users in this group</p>
      <AGDataGrid<GroupPermissionRow>
        rowData={paginatedPerms}
        columnDefs={columnDefs}
        totalCount={filteredPerms.length}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search permissions..."
        isLoading={permsLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onSelectionChanged={handleSelectionChanged}
        rowSelection={{
          mode: 'multiRow' as const,
          checkboxes: true,
          headerCheckbox: true,
        }}
        height="calc(100vh - 480px)"
        toolbarActions={
          <div className="flex items-center gap-2">
            {selectedRows.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove ({selectedRows.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Permission
            </Button>
          </div>
        }
      />

      <AddPermissionDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        availablePermissions={availablePermissions}
        isAdding={isAdding}
        onAdd={handleAddPermission}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Modules Tab                                                        */
/* ------------------------------------------------------------------ */

interface ModulesTabProps {
  groupId: string;
  companyId: string;
}

function ModulesTab({ groupId, companyId }: ModulesTabProps) {
  const { data: modules, isLoading } = useQuery({
    queryKey: ['group-modules', groupId, companyId],
    queryFn: async () => {
      const r = await groupModuleService.listModules(groupId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      <p className="text-sm text-muted-foreground mb-4">Modules licensed to users in this group</p>
      {!modules?.length ? (
        <p className="text-sm text-muted-foreground p-4">No modules assigned to this group.</p>
      ) : (
        <div className="space-y-2 p-4">
          {modules.map((m) => (
            <div
              key={m.licensed_module_id}
              className="flex items-center justify-between rounded-md border px-4 py-3"
            >
              <div>
                <span className="font-medium text-sm">{m.module_name}</span>
                <span className="ml-2 text-xs text-muted-foreground font-mono">{m.module_code}</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {formatDateTime(m.assigned_at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                     */
/* ------------------------------------------------------------------ */

export default function SecurityGroupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateCache: invalidateAccessCache } = useAccessControl();

  const [editName, setEditName] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);

  // Get companyId
  const { data: companyId } = useQuery({
    queryKey: ['my-company-id', authUser?.id],
    queryFn: async () => {
      const { data: profile } = await profileService.get(authUser!.id);
      return profile?.company_id ?? null;
    },
    enabled: !!authUser,
    staleTime: 10 * 60 * 1000,
  });

  // Fetch group
  const { data: group, isLoading: isGroupLoading } = useQuery({
    queryKey: ['group-detail', id],
    queryFn: async () => {
      const result = await groupService.get(id!);
      if (result.error) throw result.error;
      if (!result.data) throw new Error('Group not found');
      return result.data;
    },
    enabled: !!id,
  });

  // Sync edit state when group loads
  useMemo(() => {
    if (group && !hasEdits) {
      setEditName(group.name);
      setEditIsActive(group.is_active);
    }
  }, [group, hasEdits]);

  const serviceContext = useMemo<ServiceContext>(
    () => ({ companyId: companyId!, userId: authUser?.id }),
    [companyId, authUser?.id]
  );

  const handleSave = async () => {
    if (!group || !companyId) return;
    setIsSaving(true);
    try {
      const updates: Partial<{ name: string; is_active: boolean }> = {};
      if (editName !== group.name) updates.name = editName;
      if (editIsActive !== group.is_active) updates.is_active = editIsActive;

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        return;
      }

      const result = await groupService.update(
        group.id,
        updates,
        group as unknown as Record<string, unknown>,
        serviceContext
      );

      if (result.error) {
        toast.error(result.error.message);
      } else {
        toast.success('Group updated');
        setHasEdits(false);
        queryClient.invalidateQueries({ queryKey: ['group-detail', id] });
        invalidateAccessCache();
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!id) {
    return <p className="p-4 text-muted-foreground">No group ID provided.</p>;
  }

  if (isGroupLoading) {
    return (
      <div className="animate-slide-up space-y-6 p-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="p-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/security/groups')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Groups
        </Button>
        <p className="text-muted-foreground mt-4">Group not found.</p>
      </div>
    );
  }

  return (
    <div className="animate-slide-up space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/security/groups')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Groups
      </Button>

      {/* Header: Name + Active toggle + Save */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <Input
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value);
              setHasEdits(true);
            }}
            className="text-2xl font-bold h-auto py-1 px-2 border-transparent hover:border-border focus:border-border"
            placeholder="Group name"
          />
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="is-active"
              checked={editIsActive}
              onCheckedChange={(checked) => {
                setEditIsActive(checked);
                setHasEdits(true);
              }}
            />
            <Label htmlFor="is-active" className="text-sm">Active</Label>
          </div>
          {hasEdits && (
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          )}
        </div>
      </div>

      {/* Metadata */}
      <RecordMetadata record={group} />

      {/* Tabs */}
      {companyId && (
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-4">
            <TabsTrigger
              value="users"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger
              value="permissions"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Shield className="h-4 w-4" />
              Permissions
            </TabsTrigger>
            <TabsTrigger
              value="modules"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Package className="h-4 w-4" />
              Modules
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UsersTab groupId={id} companyId={companyId} context={serviceContext} />
          </TabsContent>

          <TabsContent value="permissions">
            <PermissionsTab groupId={id} companyId={companyId} context={serviceContext} />
          </TabsContent>

          <TabsContent value="modules">
            <ModulesTab groupId={id} companyId={companyId} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
